/*
  Floor estimator (sections/floor-estimator.liquid), ported from the floor-quote prototype in
  crescendo-internal-tools/shopify/floor-quote. Two parts:
    1. the email gate: reveals the estimator once the visitor has signed up (see unlock()), and
    2. the estimator itself: lays out panels and transducers for a floor and prices them.

  The layout rules are a deliberately small port of the Installations engine
  (production-planner/web/src/pages/sales/installations/engine):
    - grid:      rectGrid.ts enumerateRectGrids. Every nx × ny split of the floor into EQUAL
                 panels, in both orientations. Each panel is classified (panelTypes.ts
                 classifyPanel) as the smallest size it fits: medium ≤ 1075 × 1075 mm, large
                 ≤ 1075 × 2300 mm. The cheapest full estimate wins; large only when the
                 "Use large panels" option is on (the default).
    - sizing:    a fixed count per panel size (transducerSizing.ts 'fixed' mode), from the
                 "Transducer density" option: high (default) is one per medium panel and two
                 per large; low is one per panel of either size.
    - placement: channels.ts. k transducers per panel at (i + 0.5)/k along the panel's long
                 side, centred across it (k = 1 → dead centre).
    - amps:      the cheapest mix of whole amps that drives every transducer (ampMix, an exact
                 DP over each transducer's amp options in CONFIG). The customer sees only
                 "Amplification"; the amp mix is named only in the quote request to the team.
  Electrical routing and wiring are intentionally NOT validated here.
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-floor-estimator]');
  if (!root) return;

  // ── Gate ────────────────────────────────────────────────────────────────────────────────
  // A soft gate: it stops casual visitors, not someone reading the page source. Unlock when:
  //   - the sign-up form just posted successfully (Liquid sets data-unlocked),
  //   - the visitor arrives from the link email with ?unlock=1, or
  //   - this browser unlocked before (localStorage; unavailable storage only means re-asking).
  var UNLOCK_KEY = 'crq-floor-estimator-unlocked';
  var EMAIL_KEY = 'crq-floor-estimator-email';      // the gate email, to prefill the quote request
  var STATE_KEY = 'crq-floor-estimator-state';      // sessionStorage, across a quote form post
  function stored(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function remembered() { return stored(UNLOCK_KEY) === '1'; }
  function remember() {
    try { window.localStorage.setItem(UNLOCK_KEY, '1'); } catch (e) { /* private mode etc. */ }
  }
  // Shopify's form state is per form type, so a footer newsletter post also reads as
  // customer_posted. Only the URL anchor (the form's id) tells us the post was the gate's.
  var search = window.location.search;
  var gatePost = window.location.hash === '#' + root.getAttribute('data-gate-form');
  var gateSignedUp = gatePost && /[?&]customer_posted=true(&|$)/.test(search);
  var fromLink = /[?&]unlock=1(&|$)/.test(search);
  var quotePost = !!root.querySelector('[data-fq-quote-sent], [data-fq-quote-error]');   // only reachable unlocked
  var gateError = root.querySelector('[data-fq-gate-error]');
  if (gateError && !gatePost) gateError.remove();               // another customer form's error
  if (root.hasAttribute('data-unlocked') || gateSignedUp || fromLink || quotePost || remembered()) {
    remember();
    root.setAttribute('data-unlocked', '');
    if (gateSignedUp) {
      // Land on the estimator, not on another form's autofocused success message.
      window.requestAnimationFrame(function () { root.scrollIntoView({ block: 'start' }); });
    }
  } else {
    // Locked: the estimator is a faded preview behind the gate card (CSS). Keep keyboard and
    // screen-reader users out of it too.
    root.querySelector('.fq-tool').inert = true;
  }

  // ── CONFIG ──────────────────────────────────────────────────────────────────────────────
  // Edit here. Tile-kit prices are USD MSRPs from the prod Sales pricelist as of 2026-10-12;
  // transducer and amp prices as of 2026-10-02. The item number each one came from is noted
  // beside it.
  var CONFIG = {
    defaultFloorFt: { w: 12, l: 16 },
    maxFloorM: 60,                           // keeps the drawing sane; bigger = talk to us
    minFloorM: 0.5,
    softMinMm: 800,                          // rules.ts softMin: avoid cuts narrower than this when we can
    // Panel sizes are "cut to size up to" maxima (rules.ts / install_settings custom panels).
    sizes: {
      M: { label: 'medium', shortMm: 1075, longMm: 1075 },
      L: { label: 'large',  shortMm: 1075, longMm: 2300 },
    },
    panelLines: [
      { id: 'standard', label: 'Standard', desc: 'Perfect for lounge spaces and light-use dance floors.',
        kit: { M: { item: 'TILEKIT-STD-M', price: 800 },
               L: { item: 'TILEKIT-STD-L', price: 1400 } } },
      { id: 'pro', label: 'Pro', desc: 'Stronger and more durable for high-use commercial applications.',
        kit: { M: { item: 'TILEKIT-PRO-M', price: 825 },
               L: { item: 'TILEKIT-PRO-L', price: 1550 } } },
    ],
    // Transducer density (Options card): transducers per panel, by panel size, for every transducer.
    densities: [
      { id: 'low',  label: 'Low',  perPanel: { M: 1, L: 1 } },
      { id: 'high', label: 'High', perPanel: { M: 1, L: 2 } },
    ],
    defaultDensity: 'high',
    defaultLargePanels: true,                // Options card "Use large panels"
    // amp.options: whole amps, `transducers` = how many one amp drives; the cheapest mix that
    // covers every transducer is priced. The page never names an amp to the customer.
    transducers: [
      { id: 'bst', label: 'BST-300EX', desc: 'Standard strength and incredible value. Works with Standard and Pro panels.',
        item: 'TDCR-BST300-KIT', price: 150, lines: ['standard', 'pro'],
        // Whole amps, cheapest mix (ampMix). The $2,694 unit matches AMP-WHARFEDALE-DP-4100F.
        amp: { options: [
          { model: 'BST amp (32 transducers)', transducers: 32, price: 2694 },
          { model: 'BST amp (16 transducers)', transducers: 16, price: 2394 },
        ] } },
      { id: 'mover', label: 'Powersoft Mover', desc: 'Commercial grade and ultra strong. Works with Pro panels.',
        item: 'TDCR-MOVER-KIT', price: 945, lines: ['pro'],
        // Whole amps, cheapest mix that covers every transducer (ampMix). `transducers` is how
        // many Movers one amp drives.
        amp: { options: [
          { model: 'Powersoft Quattrocanali 8804 DSP', transducers: 16, price: 5771 },
          { model: 'Powersoft Duecanali 4804 DSP',     transducers: 8,  price: 4762 },
          { model: 'Powersoft Duecanali 2404 DSP',     transducers: 4,  price: 3652 },
        ] } },
    ],
  };
  // ────────────────────────────────────────────────────────────────────────────────────────

  // Option names and descriptions are theme-editor settings (the section's data-fq-text JSON);
  // a blank setting keeps the CONFIG text above.
  try {
    var text = JSON.parse(document.querySelector('script[data-fq-text]').textContent);
    ['transducers', 'panelLines'].forEach(function (key) {
      CONFIG[key].forEach(function (o) {
        var t = text[key] && text[key][o.id];
        if (!t) return;
        if (t.label) o.label = t.label;
        if (t.desc) o.desc = t.desc;
      });
    });
  } catch (e) { /* keep CONFIG text */ }

  var $ = function (sel) { return root.querySelector(sel); };
  // Labels and descriptions come from the theme editor, so escape them into innerHTML.
  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  var MM_PER = { ft: 304.8, m: 1000 };
  var EPS = 1e-9;
  var state = {
    unit: 'ft',
    wMm: CONFIG.defaultFloorFt.w * MM_PER.ft,
    lMm: CONFIG.defaultFloorFt.l * MM_PER.ft,
    transducer: CONFIG.transducers[0].id,
    line: CONFIG.panelLines[0].id,
    density: CONFIG.defaultDensity,          // CONFIG.densities[].id
    allowLarge: CONFIG.defaultLargePanels,
  };
  var lastQuote = null;                      // the last rendered quote, for the quote request

  var money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  var int = new Intl.NumberFormat('en-US');
  function byId(list, id) { for (var i = 0; i < list.length; i++) if (list[i].id === id) return list[i]; return list[0]; }
  function fmtLen(mm, unit) {
    if (unit === 'm') return (mm / 1000).toFixed(2) + ' m';
    var inches = mm / 25.4;
    if (inches < 120) return inches.toFixed(1) + '″';
    return (mm / 304.8).toFixed(1) + ' ft';
  }
  function round(n, d) { var p = Math.pow(10, d); return Math.round(n * p) / p; }
  // Cheapest whole-amp mix that drives n transducers: an unbounded covering knapsack over
  // amp.options, solved exactly by DP (best[k] = cheapest way to cover k). Ties go to fewer
  // amps. Returns { cost, amps: { model: count } }. Memoised per transducer type.
  function ampMix(t, n) {
    var memo = t._ampMix || (t._ampMix = [{ cost: 0, count: 0, amps: {} }]);
    for (var k = memo.length; k <= n; k++) {
      var best = null;
      t.amp.options.forEach(function (o) {
        var rest = memo[Math.max(0, k - o.transducers)];
        var cost = rest.cost + o.price, count = rest.count + 1;
        if (!best || cost < best.cost - 0.5 || (Math.abs(cost - best.cost) <= 0.5 && count < best.count)) {
          var amps = {}; for (var m in rest.amps) amps[m] = rest.amps[m];
          amps[o.model] = (amps[o.model] || 0) + 1;
          best = { cost: cost, count: count, amps: amps };
        }
      });
      memo.push(best);
    }
    return memo[n];
  }

  // ── Engine (pure) ───────────────────────────────────────────────────────────────────────
  // Smallest allowed size a pw × pl panel fits in (classifyPanel), or null.
  function classify(pw, pl, allowLarge) {
    var s = Math.min(pw, pl), l = Math.max(pw, pl);
    var order = allowLarge ? ['M', 'L'] : ['M'];
    for (var i = 0; i < order.length; i++) {
      var z = CONFIG.sizes[order[i]];
      if (s <= z.shortMm + 0.5 && l <= z.longMm + 0.5) return order[i];
    }
    return null;
  }

  // Price one equal-split grid; null when its panels fit no allowed size.
  function priceGrid(s, t, line, nx, ny) {
    var pw = s.wMm / nx, pl = s.lMm / ny;
    var size = classify(pw, pl, s.allowLarge);
    if (!size) return null;
    var perPanel = byId(CONFIG.densities, s.density).perPanel[size];
    var panels = nx * ny, transducers = panels * perPanel;
    var kit = line.kit[size];
    var ampCost = ampMix(t, transducers).cost;
    return {
      nx: nx, ny: ny, pw: pw, pl: pl, size: size, kit: kit, perPanel: perPanel,
      panels: panels, transducers: transducers,
      tileCost: panels * kit.price, tdcrCost: transducers * t.price, ampCost: ampCost,
      total: panels * kit.price + transducers * t.price + ampCost,
      narrow: Math.min(pw, pl) < CONFIG.softMinMm - 0.5,
    };
  }

  // Per-axis counts: from what the biggest panel needs up to one past what medium needs.
  function axisCounts(dim) {
    var lo = Math.max(1, Math.ceil(dim / CONFIG.sizes.L.longMm - EPS));
    var hi = Math.max(1, Math.ceil(dim / CONFIG.sizes.M.longMm - EPS)) + 1;
    var out = [];
    for (var n = lo; n <= hi; n++) out.push(n);
    return out;
  }

  function better(a, b) {
    if (!b) return true;
    if (a.narrow !== b.narrow) return !a.narrow;              // avoid sub-800 mm cuts when possible
    if (Math.abs(a.total - b.total) > 0.5) return a.total < b.total;
    return a.panels < b.panels;
  }

  function computeQuote(s) {
    var t = byId(CONFIG.transducers, s.transducer);
    var line = byId(CONFIG.panelLines, s.line);
    var xs = axisCounts(s.wMm), ys = axisCounts(s.lMm);
    var best = null;
    for (var i = 0; i < xs.length; i++) {
      for (var j = 0; j < ys.length; j++) {
        var g = priceGrid(s, t, line, xs[i], ys[j]);
        if (!g) continue;
        if (better(g, best)) best = g;
      }
    }
    var slots = [];
    var vertical = best.pl >= best.pw;                        // spread transducers along the long side
    for (var iy = 0; iy < best.ny; iy++) {
      for (var ix = 0; ix < best.nx; ix++) {
        for (var k = 0; k < best.perPanel; k++) {
          var f = (k + 0.5) / best.perPanel;
          slots.push(vertical
            ? { x: (ix + 0.5) * best.pw, y: (iy + f) * best.pl }
            : { x: (ix + f) * best.pw, y: (iy + 0.5) * best.pl });
        }
      }
    }
    best.t = t; best.line = line; best.slots = slots; best.density = byId(CONFIG.densities, s.density);
    return best;
  }

  // ── Rendering ───────────────────────────────────────────────────────────────────────────
  function renderOptions(host, name, list, current, priceOf, disabledNote) {
    host.innerHTML = list.map(function (o) {
      var note = disabledNote(o);
      return '<label class="fq-opt"><input type="radio" name="' + name + '" value="' + o.id + '"' +
        (o.id === current ? ' checked' : '') + (note ? ' disabled' : '') + '><span class="fq-opt-body">' +
        '<span class="fq-opt-top"><span>' + esc(o.label) + '</span><span class="fq-opt-price">' + priceOf(o) + '</span></span>' +
        '<span class="fq-opt-desc">' + esc(o.desc) + '</span>' +
        (note ? '<span class="fq-opt-note">' + esc(note) + '</span>' : '') + '</span></label>';
    }).join('');
  }

  function renderChoices() {
    var t = byId(CONFIG.transducers, state.transducer);
    if (t.lines.indexOf(state.line) < 0) state.line = t.lines[0];   // Mover → Pro
    renderOptions($('#fq-transducers'), 'fq-t', CONFIG.transducers, state.transducer,
      function (o) { return money.format(o.price) + ' ea'; },
      function () { return ''; });
    renderOptions($('#fq-panels'), 'fq-p', CONFIG.panelLines, state.line,
      function (p) { return 'from ' + money.format(p.kit.M.price) + ' / tile'; },
      function (p) { return t.lines.indexOf(p.id) < 0 ? 'Not compatible with the ' + t.label : ''; });
  }

  function renderSvg(q) {
    var W = state.wMm, L = state.lMm;
    var pad = Math.max(W, L) * 0.07;              // room for dimension labels
    var fs = Math.max(W, L) * 0.028;
    var gap = Math.min(q.pw, q.pl) * 0.02;
    var along = Math.max(q.pw, q.pl) / q.perPanel, across = Math.min(q.pw, q.pl);
    var r = Math.min(90, across * 0.12, along * 0.12);  // 180 mm footprint, shrunk on tiny cuts
    var out = [];
    out.push('<svg viewBox="' + (-pad) + ' ' + (-pad) + ' ' + (W + pad * 1.4) + ' ' + (L + pad * 1.4) +
      '" role="img" aria-label="Floor layout: ' + q.nx + ' by ' + q.ny + ' ' + CONFIG.sizes[q.size].label +
      ' panels with ' + q.transducers + ' transducers.">');
    out.push('<rect x="0" y="0" width="' + W + '" height="' + L + '" fill="none" stroke="var(--fq-navy)" stroke-width="' + gap * 1.5 + '"/>');
    for (var iy = 0; iy < q.ny; iy++) {
      for (var ix = 0; ix < q.nx; ix++) {
        out.push('<rect x="' + round(ix * q.pw + gap, 1) + '" y="' + round(iy * q.pl + gap, 1) +
          '" width="' + round(q.pw - 2 * gap, 1) + '" height="' + round(q.pl - 2 * gap, 1) + '" rx="' + gap * 1.5 +
          '" fill="var(--fq-panel)" stroke="var(--fq-navy)" stroke-opacity=".35" stroke-width="' + round(gap * 0.6, 1) + '"/>');
      }
    }
    for (var i = 0; i < q.slots.length; i++) {
      var s = q.slots[i];
      out.push('<circle cx="' + round(s.x, 1) + '" cy="' + round(s.y, 1) + '" r="' + round(r, 1) +
        '" fill="var(--fq-accent)" stroke="var(--fq-navy)" stroke-opacity=".6" stroke-width="' + round(r * 0.18, 1) + '"/>');
    }
    // Dimension labels: width along the top, length up the left side.
    var lblStyle = 'font-family="inherit" font-size="' + fs + '" fill="var(--fq-slate)" text-anchor="middle" aria-hidden="true"';
    out.push('<text x="' + W / 2 + '" y="' + (-pad * 0.35) + '" ' + lblStyle + '>' + fmtLen(W, state.unit) + '</text>');
    out.push('<text x="' + (-pad * 0.35) + '" y="' + L / 2 + '" ' + lblStyle +
      ' transform="rotate(-90 ' + (-pad * 0.35) + ' ' + L / 2 + ')">' + fmtLen(L, state.unit) + '</text>');
    out.push('</svg>');
    return out.join('');
  }

  function row(item, qty, unit, total) {
    return '<tr><td>' + item + '</td>' +
      '<td class="fq-num-col fq-hide-sm">' + qty + '</td><td class="fq-num-col fq-hide-sm">' + unit + '</td>' +
      '<td class="fq-num-col">' + total + '</td></tr>';
  }

  function validate() {
    var minMm = CONFIG.minFloorM * 1000, maxMm = CONFIG.maxFloorM * 1000;
    var w = state.wMm, l = state.lMm;
    if (!(w > 0) || !(l > 0)) return 'Enter a width and length to see your layout.';
    if (w < minMm || l < minMm) return 'Each side needs to be at least ' + fmtLen(minMm, state.unit) + '.';
    if (w > maxMm || l > maxMm) return 'For floors over ' + fmtLen(maxMm, state.unit) + ' on a side, contact us and we\'ll design it with you.';
    return null;
  }

  function render() {
    var err = validate();
    var hint = $('#fq-dim-hint');
    var foot = $('#fq-foot');                                 // the estimate note (theme editor)
    root.querySelectorAll('[data-unit-label]').forEach(function (el) { el.textContent = state.unit; });
    root.querySelectorAll('.fq-seg [data-unit]').forEach(function (b) { b.setAttribute('aria-pressed', String(b.dataset.unit === state.unit)); });
    root.querySelectorAll('#fq-density [data-density]').forEach(function (b) { b.setAttribute('aria-pressed', String(b.dataset.density === state.density)); });
    root.querySelectorAll('#fq-large [data-large]').forEach(function (b) { b.setAttribute('aria-pressed', String((b.dataset.large === 'on') === state.allowLarge)); });
    if (err) {
      hint.textContent = err; hint.className = 'fq-hint fq-warn';
      $('#fq-stats').innerHTML = ''; $('#fq-canvas').innerHTML = ''; $('#fq-legend').innerHTML = '';
      $('#fq-price-rows').innerHTML = '';
      if (foot) foot.hidden = true;
      return;
    }
    var q = computeQuote(state);
    var sizeLabel = CONFIG.sizes[q.size].label;
    var areaM2 = state.wMm * state.lMm / 1e6;
    var area = state.unit === 'm' ? round(areaM2, 1) + ' m²' : int.format(Math.round(areaM2 * 10.7639)) + ' sq ft';
    hint.className = 'fq-hint';
    hint.textContent = q.narrow
      ? 'Heads up: panels this narrow are small custom cuts. We\'ll confirm the best layout with you.'
      : area;

    // A fixed set of rows, so the card keeps its height whatever the numbers are.
    var tdcrName = esc(q.t.label) + ' (' + q.density.label.toLowerCase() + ' density)';
    var summaryRows = [
      ['Layout', q.panels + ' ' + sizeLabel + ' panels in a ' + q.nx + ' × ' + q.ny + ' grid'],
      ['Panel size', fmtLen(q.pw, state.unit) + ' × ' + fmtLen(q.pl, state.unit)],
      ['Transducers', q.transducers + ' ' + tdcrName],
    ];
    $('#fq-stats').innerHTML = '<table class="fq-summary"><tbody>' + summaryRows.map(function (r) {
      return '<tr><th scope="row">' + r[0] + '</th><td>' + r[1] + '</td></tr>';
    }).join('') + '</tbody></table>';
    $('#fq-canvas').innerHTML = renderSvg(q);
    $('#fq-legend').innerHTML =
      '<span><i class="fq-sw" style="background:var(--fq-panel)"></i>' + esc(q.line.label) + ' ' + sizeLabel + ' panel</span>' +
      '<span><i class="fq-dot"></i>' + esc(q.t.label) + ' transducer</span>';

    $('#fq-price-rows').innerHTML =
      row(esc(q.line.label) + ' ' + sizeLabel + ' Bass Tile',
        q.panels, money.format(q.kit.price), money.format(q.tileCost)) +
      row(esc(q.t.label) + ' transducer',
        q.transducers, money.format(q.t.price), money.format(q.tdcrCost)) +
      row('Amplification <span class="fq-est">(estimate)</span>',
        '—', '—', money.format(q.ampCost)) +
      '<tr class="fq-total"><td>Estimated total</td><td class="fq-hide-sm"></td><td class="fq-hide-sm"></td>' +
      '<td class="fq-num-col">' + money.format(q.total) + '</td></tr>';
    if (foot) foot.hidden = false;

    lastQuote = q;
  }

  // Plain-text estimate for the quote request email: what the team needs to pick the job up.
  // Unlike the page, it names the amp mix the estimate was priced on.
  function estimateText(q) {
    if (!q || validate()) return 'No valid floor size entered.';
    var sizeLabel = CONFIG.sizes[q.size].label;
    var amps = ampMix(q.t, q.transducers).amps, ampList = [];
    for (var m in amps) ampList.push(amps[m] + ' x ' + m);
    return [
      'Floor: ' + fmtLen(state.wMm, state.unit) + ' x ' + fmtLen(state.lMm, state.unit),
      'Options: ' + q.density.label.toLowerCase() + ' transducer density, large panels ' + (state.allowLarge ? 'on' : 'off'),
      'Layout: ' + q.nx + ' x ' + q.ny + ' grid of ' + q.panels + ' ' + q.line.label + ' ' + sizeLabel +
        ' panels, each ' + fmtLen(q.pw, state.unit) + ' x ' + fmtLen(q.pl, state.unit),
      'Tiles: ' + q.panels + ' x ' + q.kit.item + ' @ ' + money.format(q.kit.price),
      'Transducers: ' + q.transducers + ' x ' + q.t.item + ' (' + q.t.label + ') @ ' + money.format(q.t.price),
      'Amplification: est. ' + money.format(q.ampCost) + ' (' + ampList.join(', ') + ')',
      'Estimated total: ' + money.format(q.total),
    ].filter(Boolean).join('\n');
  }

  // ── Wiring up ───────────────────────────────────────────────────────────────────────────
  function syncInputs() {
    var d = state.unit === 'm' ? 2 : 1;
    $('#fq-w').value = round(state.wMm / MM_PER[state.unit], d);
    $('#fq-l').value = round(state.lMm / MM_PER[state.unit], d);
    $('#fq-w').step = $('#fq-l').step = state.unit === 'm' ? '0.1' : '0.5';
  }
  ['#fq-w', '#fq-l'].forEach(function (sel, i) {
    $(sel).addEventListener('input', function (e) {
      var v = parseFloat(e.target.value);
      state[i ? 'lMm' : 'wMm'] = isFinite(v) ? v * MM_PER[state.unit] : NaN;
      render();
    });
  });
  root.querySelectorAll('.fq-seg [data-unit]').forEach(function (b) {
    b.addEventListener('click', function () { state.unit = b.dataset.unit; syncInputs(); renderChoices(); render(); });
  });
  $('#fq-density').innerHTML = CONFIG.densities.map(function (o) {
    return '<button type="button" data-density="' + o.id + '">' + o.label + '</button>';
  }).join('');
  $('#fq-density').addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-density]');
    if (b) { state.density = b.dataset.density; render(); }
  });
  $('#fq-large').addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-large]');
    if (b) { state.allowLarge = b.dataset.large === 'on'; render(); }
  });
  // "Help me pick" buttons open the dialog named by their data-dialog; a backdrop click closes it
  root.querySelectorAll('.fq-help[data-dialog]').forEach(function (btn) {
    var dlg = $('#' + btn.getAttribute('data-dialog'));
    btn.addEventListener('click', function () {
      if (dlg.showModal) dlg.showModal(); else dlg.setAttribute('open', '');
    });
    dlg.addEventListener('click', function (e) { if (e.target === dlg) dlg.close(); });
  });
  root.addEventListener('change', function (e) {
    if (e.target.closest('.fq-dialog, .fq-gate')) return;    // form fields, not estimator inputs
    if (e.target.name === 'fq-t') state.transducer = e.target.value;
    if (e.target.name === 'fq-p') state.line = e.target.value;
    if (e.target.name === 'fq-t' || e.target.name === 'fq-p') {
      renderChoices();                                         // rebuilds the radios: restore focus
      var again = root.querySelector('input[name="' + e.target.name + '"][value="' + e.target.value + '"]');
      if (again) again.focus();
    }
    render();
  });

  // ── Quote request dialog ────────────────────────────────────────────────────────────────
  // Submitting is a full page post, so the estimator would come back at its defaults. Save the
  // configuration first and restore it on the next load, so a visitor who hits a form error
  // isn't left resubmitting a floor they didn't design.
  var dialog = $('#fq-quote');
  function open() {
    var est = $('#fq-quote-estimate'), email = $('#fq-quote-email');
    if (est) est.value = estimateText(lastQuote);
    if (email && !email.value) email.value = stored(EMAIL_KEY) || '';
    if (dialog.showModal) dialog.showModal(); else dialog.setAttribute('open', '');
  }
  $('#fq-cta').addEventListener('click', open);
  dialog.addEventListener('click', function (e) {
    if (e.target === dialog || e.target.closest('[data-fq-close]')) dialog.close();   // backdrop or ×
  });
  dialog.querySelector('form').addEventListener('submit', function () {
    $('#fq-quote-estimate').value = estimateText(lastQuote);
    try { window.sessionStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch (e) { /* lose state on error */ }
  });
  var gateForm = $('.fq-gate form');
  if (gateForm) gateForm.addEventListener('submit', function () {
    try { window.localStorage.setItem(EMAIL_KEY, $('#fq-gate-email').value); } catch (e) { /* no prefill */ }
  });

  try {
    var saved = JSON.parse(window.sessionStorage.getItem(STATE_KEY));
    window.sessionStorage.removeItem(STATE_KEY);
    if (saved) for (var k in state) if (k in saved) state[k] = saved[k] === null ? NaN : saved[k];
  } catch (e) { /* keep defaults */ }

  syncInputs();
  renderChoices();
  render();
  // Back from a quote post: show the thank-you, or the error with the form.
  if (quotePost) open();
  window.CrescendoFloorQuote = { computeQuote: computeQuote, ampMix: ampMix, CONFIG: CONFIG };
})();
